import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import HorizontalImageCarousel from '../components/HorizontalImageCarousel';

const Leadership = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  return (
    <div className="w-full bg-white" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full h-72 md:h-[500px] overflow-hidden relative shadow-2xl"
      >
        <img
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
          alt="Leadership"
          className="w-full h-full object-cover"
        />
        {/* Overlay to make text readable if you put text on image, or just for style */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/80 to-transparent flex items-end p-10">
          <div className="max-w-2xl">
            <span className="inline-block px-3 py-1 bg-[#9a8456] text-white text-xs font-bold tracking-widest uppercase rounded-sm mb-3">
              Over Decade of experience
            </span>
            <h3 className="text-3xl md:text-4xl font-bold text-white">
              Our Leadership
            </h3>
          </div>
        </div>
      </motion.div>

      <section className="py-24 pl-10 pr-10 bg-[#fffcf5] relative overflow-hidden">
        {/* CSS for Custom Scrollbar */}
        <style>{`
            .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(0,0,0,0.05);
            border-radius: 4px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #9a8456; 
            border-radius: 4px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #6d5b38; 
            }
        `}</style>

        {/* Background Decorative Element */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gray-100/50 skew-x-12 translate-x-20 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">

          {/* --- SECTION HEADER --- */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="mb-16 max-w-3xl"
          >
            <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-semibold text-[#0f172a] mb-6">
              Leadership & {''}
              <span className="text-[#2a3890]">Management</span>
            </motion.h2>
          </motion.div>

          {/* --- CONTENT GRID --- */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            {/* LEFT COLUMN: EXECUTIVE LEADERSHIP (Main Text) */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-8"
            >
              <div className="prose prose-lg text-gray-600">
                <h3 className="text-2xl font-bold text-[#0f172a] flex items-center gap-3 mb-4">
                  <Users className="text-[#9a8456]" size={28} />
                  Executive Leadership
                </h3>
                <p className="leading-relaxed">
                  TM International Group is led by an experienced executive team with deep expertise in
                  construction management, business development, real estate investment, and strategic planning.
                  Our leadership brings decades of combined experience in delivering complex construction projects
                  across residential, commercial, and government sectors.
                </p>
                <p className="leading-relaxed mt-4 border-l-4 border-[#2a3890] pl-6 italic bg-white p-4 rounded-r-lg shadow-sm">
                  "The executive team's vision and strategic direction have been instrumental in achieving our
                  remarkable growth from <span className="font-bold text-[#2a3890]">1 million to 15-20 million</span> in
                  annual revenue, expanding our geographic footprint across five states, and establishing a
                  portfolio of successful companies serving diverse market segments."
                </p>
              </div>
            </motion.div>

            {/* RIGHT COLUMN: CARDS (Scrollable) */}
            {/* Added fixed height and overflow-y-auto for scroll effect */}
            <div className="lg:col-span-5 space-y-6 lg:h-[450px] lg:overflow-y-auto lg:pr-4 custom-scrollbar">

              {/* Card 1: Management Philosophy */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-1xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
              >
                {/* <div className="w-12 h-12 bg-[#2a3890]/10 rounded-lg flex items-center justify-center mb-6">
                    <Target className="text-[#2a3890]" size={24} />
                </div> */}
                <h4 className="text-xl font-bold text-[#0f172a] mb-3">Management Philosophy</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our management approach emphasizes strategic planning, operational excellence, and continuous improvement.
                  We invest heavily in our team's professional development, ensuring they have the skills, tools, and support
                  needed to deliver exceptional results. Our commitment to innovation drives us to adopt cutting-edge construction technologies,
                  sustainable building practices, and efficient project management methodologies.
                </p>
              </motion.div>

              {/* Card 2: Organizational Structure */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-[#0f172a] p-8 rounded-1xl shadow-lg border border-gray-800 text-white relative overflow-hidden group"
              >
                {/* Subtle hover effect background */}
                <div className="absolute inset-0 bg-[#2a3890] opacity-0 group-hover:opacity-10 transition-opacity duration-500" />

                {/* <div className="w-12 h-12 bg-[#9a8456]/20 rounded-lg flex items-center justify-center mb-6 relative z-10">
                    <GitMerge className="text-[#9a8456]" size={24} />
                </div> */}
                <h4 className="text-xl font-bold text-white mb-3 relative z-10">Organizational Structure</h4>
                <p className="text-gray-300 text-sm leading-relaxed relative z-10">
                  TM International Group operates through a decentralized structure that empowers our regional
                  teams while maintaining corporate oversight and quality standards. Each portfolio company has dedicated
                  management teams with authority to make operational decisions, supported by centralized resources in finance, legal, human resources, and business development.
                </p>
              </motion.div>

              {/* Optional: Padding bottom to ensure last card is fully visible when scrolled */}
              {/* <div className="h-4"></div> */}
            </div>
          </div>
        </div>
      </section>

      <HorizontalImageCarousel />

      <div className="bg-gray-900 max-w-full mx-auto px-4 text-center py-12 text-white">
        <h2 className="text-2xl font-display uppercase tracking-widest mb-4">Ready to build your vision?</h2>
        <button className="border border-white px-8 py-3 hover:bg-white hover:text-black transition uppercase text-sm tracking-wider"
          onClick={() => window.location.href = '/contact'}>
          Contact Us
        </button>
      </div>

    </div>
  )
};

export default Leadership;
